import { ReactNode, memo, useEffect, useRef } from 'react';

import { set } from 'lodash';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { RemoveButton, AddMoreButton } from '@elements';
import { Stack, Group, Text, Box, Divider } from '@ui';

import { AdaptiveFormRows } from '../../AdaptiveFormRows';
import classes from '../../styles/Common.module.css';
import { AdaptiveFormConfig, AdaptiveFormFieldConfig, Fields } from '../../types';

const createInitialValuesForArrayFormFields = (formConfig: ArrayFormConfig) => {
  const initialValue: Record<string, any> = {};

  const processField = (field: ArrayFormFieldConfig) => {
    if (field.name) {
      set(initialValue, field.name, field.defaultValue ?? null);
    }

    // Recursively process nested fields
    if (field.fields) {
      field.fields.forEach(processField);
    }
  };

  formConfig.forEach(processField);
  return initialValue;
};

const createArrayFormFieldConfig = (
  name: string,
  formConfig: ArrayFormConfig,
  index: number,
  key: string
): AdaptiveFormConfig => {
  const arrayFormFieldConfig = formConfig.map((field) => {
    const { name: fieldName, type } = field;
    let arrayFieldConfig = {
      ...field,
      name: `${name}.${index}.${fieldName}`,
      componentProps: {
        ...field.componentProps
      },
      index
    };

    if (type === Fields.heightPicker) {
      const { ftName, inName } = field;
      arrayFieldConfig = {
        ...arrayFieldConfig,
        componentProps: {
          ...field.componentProps
        },
        //@ts-ignore
        ftName: `${name}.${index}.${ftName}`,
        inName: `${name}.${index}.${inName}`
      };
    }

    if (field.fields) {
      // nested field
      arrayFieldConfig = {
        ...arrayFieldConfig,
        fields: createArrayFormFieldConfig(name, field.fields, index, key)
      };
    }

    return arrayFieldConfig;
  });

  return arrayFormFieldConfig as AdaptiveFormConfig;
};

interface ArrayFieldTitleProps {
  title: string;
  showRemove: boolean;
  onRemove: () => void;
}

const ArrayFieldTitle = ({ title, showRemove, onRemove }: ArrayFieldTitleProps) => {
  return (
    <Group justify="space-between" align="center" w="100%" mb="xs">
      <Text fw={600} fz={14}>
        {title}
      </Text>
      {showRemove && <RemoveButton handleRemove={onRemove} />}
    </Group>
  );
};

type ArrayFormFieldConfig = AdaptiveFormFieldConfig & {
  defaultValue?: any;
};

export type ArrayFormConfig = Array<ArrayFormFieldConfig>;

export type ArrayFormFieldsProps = {
  formConfig: ArrayFormConfig;
  formSubTitle?: string;
  // when this is the main field of the form eg: Nominee otherwise use formSubtitle
  title?: string;
  subFieldTitle: string;
  name: string;
  addBtnCtaText?: string;
  // Use this to override the config when it is dependent on value.
  dynamicFieldConfig?: (
    value: any,
    config: ArrayFormConfig,
    index: number
  ) => AdaptiveFormConfig;
  // Use this to render error for each sub form
  renderErrorPerSubForm?: boolean;
  maxForms: number;
  showAddMore?: boolean;
  enableDivider?: boolean;
  enableTitleDivider?: boolean;
  enableTitle?: boolean;
  // Ignore default values when adding a new form
  // The schema should have default values for the fields
  ignoreInitialValues?: boolean;
  // Ignore watchForChanges when rendering the summary
  ignoreWatchForChanges?: boolean;
  // Show the array as a table in summary
  showAsTableInSummary?: boolean;
  preventFirstItemRemoval?: boolean;
  // Disable the background color for array form fields
  disableBackground?: boolean;
};

interface ArrayFieldsProps
  extends Omit<ArrayFormFieldsProps, 'formSubTitle' | 'maxForms'> {
  value: Array<any>;
  fields: Array<any>;
  error: any;
  remove: (index: number) => void;
}

/**
 * @description Render the form for an array property
 */
const ArrayFields = memo(
  ({
    formConfig,
    value,
    fields,
    remove,
    name,
    subFieldTitle,
    dynamicFieldConfig,
    error,
    renderErrorPerSubForm,
    enableTitle,
    enableDivider,
    preventFirstItemRemoval = false,
    disableBackground = false
  }: ArrayFieldsProps) => {
    return (
      <>
        {Array.isArray(fields) &&
          fields.length > 0 &&
          fields.map((field, index) => {
            let config = createArrayFormFieldConfig(
              name,
              formConfig,
              index,
              field.id
            );
            if (typeof dynamicFieldConfig === 'function') {
              config = dynamicFieldConfig(value, config, index);
            }

            return (
              <Box key={field.id || index}>
                <Box
                  p={enableDivider ? 0 : 24}
                  mt={enableDivider ? 0 : 16}
                  className={
                    disableBackground ? undefined : classes.arrayFormFieldBox
                  }
                >
                  {enableTitle && (
                    <ArrayFieldTitle
                      title={`${subFieldTitle} ${index + 1}`}
                      // Restrict removal of first item if preventFirstItemRemoval is true
                      // Use case: Required for IPRU TIN/PAN details arrayForm - first item is prefilled and fields are disabled
                      showRemove={
                        fields.length > 1 &&
                        (preventFirstItemRemoval ? index !== 0 : true)
                      }
                      onRemove={() => {
                        remove(index);
                      }}
                    />
                  )}

                  <AdaptiveFormRows config={config} context={fields[index]} />
                </Box>

                {enableDivider && index < fields.length - 1 && <Divider my={32} />}

                {renderErrorPerSubForm &&
                  Array.isArray(error) &&
                  typeof error?.[index]?.message === 'string' && (
                    <Box style={{ marginBlock: '1rem' }}>
                      <Text size="xs" c="red">
                        {error?.[index]?.message}
                      </Text>
                    </Box>
                  )}
              </Box>
            );
          })}
      </>
    );
  }
);

/**
 *
 * @description Renders Formik's FieldArray with Add More and Remove buttons for a given form config and name.
 *
 * name - name of the field that has value as array.
 * @example
 * ```js
 * // Consider the following form structure where foo is an array that can contain any number of objects with properties name & email.
 * const initialValues = {
 *    foo: [
 *      {
 *        name: undefined,
 *        email: undefined
 *      }
 *    ]
 * }
 *
 * const config = {
 *    nameField: {
 *      name: 'name',
 *      label: 'Enter your name'
 *    },
 *    emailField: {
 *      name: 'email',
 *      label: 'Enter your email'
 *    }
 * }
 *
 * <ArrayFormFields formConfig={config} name="foo" />
 * // renders the following if the length of values.foo === 1.
 *
 * <AddMoreButton />
 * <FieldArray name="foo">
 *    <TextInput name="foo[0].name" label="Enter your name" />
 *    <TextInput name="foo[0].email" label="Enter your email" />
 * </FieldArray>
 *
 * // if foo.length === 2
 *
 * <AddMoreButton />
 * <FieldArray name="foo">
 *    <TextInput name="foo[0].name" label="Enter your name" />
 *    <TextInput name="foo[0].email" label="Enter your email" />
 *
 *    <TextInput name="foo[1].name" label="Enter your name" />
 *    <TextInput name="foo[1].email" label="Enter your email" />
 *     <RemoveButton />
 * </FieldArray>
 *
 * // !NOTE: enabling validateOnChange causes unnecessary renders.
 * // https://github.com/jaredpalmer/formik/issues/2530
 * ```
 *
 */
const ArrayFormFields = memo((props: ArrayFormFieldsProps) => {
  const {
    formConfig,
    name,
    maxForms,
    showAddMore = true,
    enableTitleDivider = false,
    title,
    formSubTitle,
    ignoreInitialValues = false,
    preventFirstItemRemoval,
    addBtnCtaText
  } = props;
  const { formState } = useFormContext();
  const initRef = useRef<boolean>(false);
  const { fields, append, remove } = useFieldArray({ name });

  const { errors } = formState;
  const error = errors?.[name];

  const titleComponent =
    title || formSubTitle ? (
      <Stack gap={1}>
        {title && (
          <Text size="lg" fw={600}>
            {title}
          </Text>
        )}
        {formSubTitle && (
          <Text size="md" fw={500}>
            {formSubTitle}
          </Text>
        )}
      </Stack>
    ) : null;

  useEffect(() => {
    if (fields?.length === 0 && !initRef.current) {
      initRef.current = true;
      append(createInitialValuesForArrayFormFields(formConfig), {
        shouldFocus: false
      });
    }
  }, []);

  const value = useWatch({ name });

  return (
    <Stack justify="center" gap={0} w="100%">
      <Group justify="space-between" align="center">
        {titleComponent}
        {fields?.length < maxForms && showAddMore && (
          <AddMoreButton
            ctaText={addBtnCtaText}
            handleAdd={() => {
              append(
                ignoreInitialValues
                  ? {}
                  : createInitialValuesForArrayFormFields(formConfig)
              );
            }}
          />
        )}
      </Group>

      {enableTitleDivider && <Divider mt={12} mb={24} />}

      <ArrayFields
        {...props}
        value={value}
        fields={fields}
        error={error}
        remove={remove}
        preventFirstItemRemoval={preventFirstItemRemoval}
        disableBackground={props.disableBackground}
      />
    </Stack>
  );
});

interface ArrayFormWIthInitialValueProps {
  name: string;
  isSelected: boolean;
  formFieldConfig: AdaptiveFormConfig;
  children: ReactNode;
}

/**
 *
 * @description Sets the initial values of the array form when the selected value changes.
 * Array forms are usually rendered on click.
 * Upon click render an empty form with the initial values.
 *
 */
const ArrayFormWithInitialValue = ({
  name,
  isSelected,
  formFieldConfig,
  children
}: ArrayFormWIthInitialValueProps) => {
  const { control, setValue } = useFormContext();
  const { fields } = useFieldArray({ control, name });

  useEffect(() => {
    const validate = async () => {
      if (isSelected && Array.isArray(fields) && !fields?.length) {
        setValue(name, [createInitialValuesForArrayFormFields(formFieldConfig)]);
        return;
      }

      if (!isSelected && Array.isArray(fields) && fields.length > 0) {
        setValue(name, []);
      }
    };
    validate();
  }, [isSelected, fields]);

  if (!isSelected) {
    return null;
  }

  return <>{children}</>;
};

export { ArrayFormFields, ArrayFormWithInitialValue };
